import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Menu, Calendar, Phone } from "lucide-react";
import AdminLayout from "@/components/admin/AdminLayout";
import { useMenuCategories } from "@/hooks/useMenu";
import { useEvents } from "@/hooks/useEvents";
import { useContactInfo } from "@/hooks/useContactInfo";
import { useQueryClient } from "@tanstack/react-query";

const AdminDashboard: React.FC = () => {
  const { data: categories = [] } = useMenuCategories();
  const { data: events = [] } = useEvents();
  const { data: contactInfo = [] } = useContactInfo();
  const queryClient = useQueryClient();

  // Always refresh data when dashboard mounts
  useEffect(() => {
    queryClient.invalidateQueries({ queryKey: ["menu-categories"] });
    queryClient.invalidateQueries({ queryKey: ["menu-items"] });
    queryClient.invalidateQueries({ queryKey: ["events"] });
    queryClient.invalidateQueries({ queryKey: ["contact-info"] });
  }, [queryClient]);

  const stats = [
    {
      label: "Menu Categories",
      value: categories.length,
      icon: <Menu className="w-5 h-5" />,
      link: "/admin/menu",
    },
    {
      label: "Events",
      value: events.length,
      icon: <Calendar className="w-5 h-5" />,
      link: "/admin/events",
    },
    {
      label: "Contact Info Items",
      value: contactInfo.length,
      icon: <Phone className="w-5 h-5" />,
      link: "/admin/contact",
    },
  ];

  return (
    <AdminLayout>
      <div className="space-y-8">
        <div>
          <h1 className="text-3xl font-display font-bold text-lounge-gold">Dashboard</h1>
          <p className="text-lounge-light/60 mt-2">Overview of your website content</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {stats.map((stat) => (
            <Link
              key={stat.label}
              to={stat.link}
              className="bg-lounge-charcoal p-6 rounded-lg border border-lounge-gold/20 hover:border-lounge-gold/40 transition-all group"
            >
              <div className="flex items-center gap-4">
                <div className="p-3 bg-lounge-gold/10 rounded-lg text-lounge-gold group-hover:bg-lounge-gold/20 transition-colors">
                  {stat.icon}
                </div>
                <div>
                  <p className="text-sm text-lounge-light/60">{stat.label}</p>
                  <p className="text-2xl font-display font-bold text-lounge-gold">{stat.value}</p>
                </div>
              </div>
            </Link>
          ))}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Recent Events */}
          <div className="bg-lounge-charcoal p-6 rounded-lg border border-lounge-gold/20">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-display font-semibold text-lounge-gold">Recent Events</h2>
              <Link to="/admin/events" className="text-sm text-lounge-gold hover:underline">
                View All
              </Link>
            </div>
            <div className="space-y-4">
              {events.slice(0, 3).map((event) => (
                <div key={event.id} className="flex items-center gap-4">
                  <div className="p-2 bg-lounge-gold/10 rounded-lg">
                    <Calendar className="w-4 h-4 text-lounge-gold" />
                  </div>
                  <div>
                    <p className="font-medium text-lounge-light">{event.title}</p>
                    <p className="text-sm text-lounge-light/60">{event.date}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Menu Categories */}
          <div className="bg-lounge-charcoal p-6 rounded-lg border border-lounge-gold/20">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-display font-semibold text-lounge-gold">Menu Categories</h2>
              <Link to="/admin/menu" className="text-sm text-lounge-gold hover:underline">
                View All
              </Link>
            </div>
            <div className="space-y-4">
              {categories.map((category) => (
                <div key={category.id} className="flex items-center gap-4">
                  <div className="p-2 bg-lounge-gold/10 rounded-lg">
                    <Menu className="w-4 h-4 text-lounge-gold" />
                  </div>
                  <div>
                    <p className="font-medium text-lounge-light">{category.name}</p>
                    <p className="text-sm text-lounge-light/60 line-clamp-1">{category.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminDashboard; 